import React, {useState, useRef} from 'react'
import {Card, Row, Col, Button, ProgressBar} from 'react-bootstrap'
import './BillingSubscription.scss'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import moment from 'moment'
import ChangePlanDrawer from './ChangePlanDrawer'
import CancelSubscriptionModal, {
  CancelFeedbackData,
} from '../../wallet-card/CancelSubscriptionModal'
import {
  useGetAccessToProductQuery,
  useGetProductPlanQuery,
  useGetProductQuery,
  useGetProductQuotaQuery,
  useGetSubscriptionQuery,
  useCancelSubscriptionMutation,
} from '../../../../../services/cellcast.v2'
import {ProductPlan, Subscription} from '../../../../../services/cellcast.v2.types'
import {formatMoney} from '../../contact-collection/utils/money'
import PaymentSuccessModal from '../../wallet-card/PaymentSuccessModal'
import BCardSubscriptionStatus from '../../wallet-card/BCardSubscriptionStatus'
import {BillingProvider} from './BillingContext'

export const manageButtonText = (selectedPlan: ProductPlan | null) => selectedPlan?.name ? `Manage ${selectedPlan?.name}` : 'Manage your plan'

interface SubscriptionCardProps {
  title: string
  price: string
  status: 'active' | 'cancelled' | 'trialing'
  nextBillDate?: string
  usage: {
    current: number
    limit: number | 'Unlimited'
    label: string
    description: string
    used?: number
  }
  onChangePlan: (e: React.MouseEvent<HTMLButtonElement>) => void
  onCancel: () => void
  onManage?: () => void
  showManage?: boolean
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  title,
  price,
  status,
  nextBillDate,
  usage,
  onChangePlan,
  onCancel,
  onManage,
  showManage = false,
}) => {
  const usagePercentage =
    usage.limit === 'Unlimited'
      ? 0
      : Math.min(((usage.used || 0) / (usage.limit as number)) * 100, 100)

  const getProgressVariant = () => {
    if (usage.limit === 'Unlimited') return 'success'
    if (usagePercentage >= 90) return 'danger'
    if (usagePercentage >= 60) return 'warning'
    return 'success'
  }

  const renderStatus = (status: 'active' | 'cancelled' | 'trialing') => {
    let badgeClass = 'secondary'

    if (status === 'active') {
      badgeClass = 'success'
    } else if (status === 'cancelled') {
      badgeClass = 'danger'
    } else if (status === 'trialing') {
      badgeClass = 'warning'
    }

    return <span className={`badge badge-light-${badgeClass} mb-2`}>{status.toUpperCase()}</span>
  }

  return (
    <Col lg={4} md={6} className='mb-4'>
      <Card className='h-100 shadow-sm'>
        <Card.Body className='p-4'>
          <div className='d-flex justify-content-between align-items-start mb-3'>
            <div>
              {renderStatus(status)}

              <h5 className='fw-bold mb-1'>{title}</h5>
            </div>
          </div>

          {nextBillDate && (
            <div className='mb-3'>
              <p className='text-muted mb-0'>
                <strong>Next bill:</strong> {moment(nextBillDate).format('MMM DD, YYYY')}
              </p>
            </div>
          )}

          <div className='mb-4'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <span className='fw-semibold text-gray-600'>{usage.label}</span>
              <span className='fw-bold text-gray-800'>
                {usage.used}/{usage.limit}
              </span>
            </div>
            {usage.limit !== 'Unlimited' && (
              <ProgressBar now={usagePercentage} variant={getProgressVariant()} className='h-8px' />
            )}
          </div>

          <div className='d-flex gap-2'>
            {showManage ? (
              <Button
                variant='primary'
                size='sm'
                className='flex-fill'
                onClick={onManage}
                style={{
                  borderRadius: '8px',
                }}
              >
                Manage
              </Button>
            ) : (
              <Button
                variant='primary'
                size='sm'
                className='flex-fill'
                onClick={onChangePlan}
                style={{
                  borderRadius: '8px',
                }}
              >
                Change plan
              </Button>
            )}
            <Button
              variant='outline-secondary text-black'
              size='sm'
              className='flex-fill'
              onClick={onCancel}
              style={{
                border: '1px solid #e4e6ea',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              Cancel
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Col>
  )
}

interface BillingSubscriptionProps {
  onCancelSubscription?: (
    productKey: string,
    subscriptionId: string,
    selectedReasonIds?: string[],
    customReason?: string
  ) => Promise<void>
  onNavigateToManage?: (productKey: string) => void
}

const BillingSubscription: React.FC<BillingSubscriptionProps> = ({onNavigateToManage}) => {
  const {data: productData} = useGetProductQuery({
    productKey: 'bcard',
  })

  const productId = productData?.data?.product?._id || ''

  const {data: accessToProduct} = useGetAccessToProductQuery({productId}, {skip: !productId})
  const dataAccessToProduct = accessToProduct?.data

  const isAccessWalletCard = dataAccessToProduct?.hasAccess

  const {data: productPlansResponse} = useGetProductPlanQuery(
    {productId: productId as string},
    {skip: !productId}
  )
  const productPlans = productPlansResponse?.data?.plans || []
  const [selectedPlan, setSelectedPlan] = useState<ProductPlan | null>(null)
  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
    refetch: refetchSubscription,
  } = useGetSubscriptionQuery({
    productId: undefined,
  })

  const {data: productQuotaResponse, refetch: refetchProductQuota} = useGetProductQuotaQuery(
    {productId},
    {skip: !productId}
  )
  const listSubscription = subscriptionData?.data?.subscriptions || []
  const quotas = subscriptionData?.data?.quotas || {}

  const navigate = useNavigate()

  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancellingProduct, setCancellingProduct] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [showPlanDrawer, setShowPlanDrawer] = useState(false)
  const [currentPlan, setCurrentPlan] = useState<ProductPlan | undefined>(undefined)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [cancelSubscription] = useCancelSubscriptionMutation()
  const bcardRef = useRef<{reFetchAllData: () => Promise<void>}>(null)

  const extractErrorMessage = (response: any): string => {
    let errorMessage = 'Failed to cancel subscription'

    if (response.error && typeof response.error === 'object') {
      if (
        'data' in response.error &&
        response.error.data &&
        typeof response.error.data === 'object'
      ) {
        if (
          'error' in response.error.data &&
          typeof response.error.data.error === 'object' &&
          response.error.data.error &&
          'message' in response.error.data.error
        ) {
          errorMessage = String(response.error.data.error.message)
        } else if ('message' in response.error.data) {
          errorMessage = String(response.error.data.message)
        }
      }
    }

    return errorMessage
  }

  const handleCancelSubscription = async (feedbackData: CancelFeedbackData): Promise<boolean> => {
    const {selectedReasonIds, customReason} = feedbackData
    try {
      setCancelError(null)
      setIsCancelling(true)

      if (cancellingProduct) {
        const subscription = listSubscription.find(
          (s) => s?.productId?.productKey === cancellingProduct
        )

        if (!subscription?.stripeSubscriptionId) {
          toast.error('Subscription stripe id not found')
          setCancelError('Subscription ID not found')
          return false
        }

        const response = await cancelSubscription({
          stripeSubscriptionId: subscription.stripeSubscriptionId,
          selectedReasonIds,
          customReason,
        })

        if ('data' in response) {
          toast.success('Subscription cancelled successfully')

          // Refetch subscription data and trigger sync
          await handleRefetchAll()

          return true
        } else {
          const errorMessage = extractErrorMessage(response)
          setCancelError(errorMessage)
          toast.error(errorMessage)
          return false
        }
      } else {
        setShowCancelConfirm(false)
        toast.error('No subscription selected for cancellation')
        return false
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Failed to cancel subscription'
      setCancelError(errorMsg)
      toast.error(errorMsg)
      return false
    } finally {
      setIsCancelling(false)
      setCancellingProduct(null)
    }
  }

  const handleChangePlan = (subscription: Subscription) => {
    const plan = productPlans.find((plan) => plan.name === subscription.planId?.name)
    if (plan) {
      setCurrentPlan(plan)
    }
  }

  const handleManage = (productKey: string) => {
    if (onNavigateToManage) {
      onNavigateToManage(productKey)
    } else {
      switch (productKey) {
        case 'bcard':
          navigate('/wallet-card')
          break
        case 'livechat':
          navigate('/live-chat')
          break
        case 'dedicated_numbers':
          navigate('/dedicated-numbers')
          break
        default:
          navigate('/dashboard')
      }
    }
  }

  const getSubscriptionStatus = (
    subscription: Subscription | undefined
  ): 'active' | 'cancelled' | 'trialing' => {
    if (!subscription) return 'cancelled'
    if (subscription.cancelAtPeriodEnd) return 'cancelled'
    if (subscription.status === 'active') return 'active'
    if (subscription.status === 'trialing') return 'trialing'
    return 'cancelled'
  }

  const getNextBillingDate = (subscription: Subscription | undefined): string => {
    if (!subscription || !subscription.currentPeriodEnd) {
      return ''
    }
    return new Date(subscription.currentPeriodEnd * 1000).toISOString().split('T')[0]
  }

  const getUsageData = (subscription: Subscription | undefined) => {
    if (!subscription) {
      return {
        current: 0,
        limit: 0,
        label: '',
        description: '',
        used: 0,
      }
    }
    return {
      current: quotas[subscription.productId?.productKey]?.remaining || 0,
      limit: quotas[subscription.productId?.productKey]?.total || 0,
      label: subscription.productId?.description || '',
      description: subscription.productId?.description || '',
      used: quotas[subscription.productId?.productKey]?.used || 0,
    }
  }

  const shouldShowManage = (productKey: string): boolean => {
    return productKey === 'dedicated_numbers'
  }

  // Shared refetch handler for syncing between components
  const handleRefetchAll = async () => {
    await Promise.all([refetchSubscription(), refetchProductQuota()])
  }

  // Function to trigger BCardSubscriptionStatus refetch
  const handleBCardRefetch = async () => {
    if (bcardRef.current) {
      await bcardRef.current.reFetchAllData()
    }
  }

  return (
    <BillingProvider onRefetch={handleRefetchAll} onChildRefetch={handleBCardRefetch}>
      <div className='card mb-5 mb-xl-10 subscriptions-wrapper'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>SUBSCRIPTIONS</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Manage your active subscriptions and usage
            </span>
          </h3>
        </div>

        <div className='card-body py-3'>
          {subscriptionLoading ? (
            <div className='text-center py-8 loading-wrapper'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
              <div className='mt-3'>
                <span className='text-muted'>Loading subscriptions...</span>
              </div>
            </div>
          ) : (
            <Row>
              {listSubscription.length > 0 ? (
                listSubscription.map((subscription) => {
                  const productKey = subscription?.productId?.productKey || 'unknown'
                  const planName = subscription?.planId?.name || 'Unknown Plan'
                  const isFreePlan = subscription?.planId?.price === 0
                  const price = subscription?.planId?.price
                    ? `${formatMoney(subscription?.planId?.price)}/${
                        subscription?.planId?.billingInterval
                      }`
                    : isFreePlan ? 'Free' : 'Unknown Price'

                  return (
                    <SubscriptionCard
                      key={subscription?._id}
                      title={`${planName} - ${price}`}
                      price={price}
                      status={getSubscriptionStatus(subscription)}
                      nextBillDate={getNextBillingDate(subscription)}
                      usage={getUsageData(subscription)}
                      showManage={shouldShowManage(productKey)}
                      onChangePlan={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault()
                        setShowPlanDrawer(true)
                        handleChangePlan(subscription)
                      }}
                      onCancel={() => {
                        const status = getSubscriptionStatus(subscription)
                        if (status === 'cancelled') {
                          toast.error('This subscription is already canceled.')
                          return
                        }
                        setCancellingProduct(productKey)
                        setShowCancelConfirm(true)
                      }}
                      onManage={() => handleManage(productKey)}
                    />
                  )
                })
              ) : (
                <Col>
                  <div className='text-center py-8'>
                    <div className='symbol symbol-100px symbol-circle mb-5'>
                      <div className='symbol-label bg-light-primary'>
                        <i className='bi bi-credit-card text-primary fs-1'></i>
                      </div>
                    </div>
                    <h4 className='mb-3'>No Active Subscriptions</h4>
                    <p className='text-muted'>
                      You don't have any active subscriptions at the moment.
                    </p>
                  </div>
                </Col>
              )}
            </Row>
          )}
        </div>
      </div>

      {isAccessWalletCard && <BCardSubscriptionStatus ref={bcardRef} />}
      {showCancelConfirm && (
        <CancelSubscriptionModal
          show={showCancelConfirm}
          onHide={() => setShowCancelConfirm(false)}
          onConfirm={handleCancelSubscription}
          isCancelling={isCancelling}
          cancelError={cancelError || undefined}
          currentPlan={currentPlan || undefined}
          nextBillingDate={getNextBillingDate(
            subscriptionData?.data?.subscriptions?.[0] as Subscription
          )}
          onAfterSuccessClose={async () => {
            await handleRefetchAll()
          }}
        />
      )}

      <ChangePlanDrawer
        show={showPlanDrawer}
        onClose={() => setShowPlanDrawer(false)}
        onBack={() => setShowPlanDrawer(false)}
        onUpdatePlan={setSelectedPlan}
        plans={productPlans}
        onUpdate={() => {
          setShowPlanDrawer(false)
        }}
        title={'Change AI B-Card Plan'}
        currentPlan={currentPlan || undefined}
        onDataRefresh={async () => {
          await handleRefetchAll()
          setShowSuccessModal(true)
        }}
        subscription={subscriptionData?.data?.subscriptions[0] || undefined}
        productQuota={productQuotaResponse?.data || undefined}
      />

      <PaymentSuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        plan={selectedPlan || null}
        onGoToSubscriptions={() => {
          navigate('/accounts/billing')
        }}
        titleGoto={manageButtonText(selectedPlan)}
        description={`Click on the button below to start creating your digital business cards.`}
      />
    </BillingProvider>
  )
}

export default BillingSubscription
